import React from 'react';
import { RefreshCw, AlertCircle, Settings2 } from 'lucide-react';
import enTranslations from '../../locales/en.json';
import { Button } from '../ui/button';

/** Platform integration admin UI always uses English copy (not user locale). */
const getNestedTranslation = (obj, path) => {
  if (typeof path !== 'string' || !path) return undefined;
  let value = obj;
  for (const key of path.split('.')) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return undefined;
    }
  }
  return typeof value === 'string' ? value : undefined;
};

export const integrationT = (key, fallback = null) =>
  getNestedTranslation(enTranslations, key) ?? fallback ?? key;

export const IntegrationLoadingSpinner = ({ className = '' }) => (
  <div className={`flex items-center justify-center h-64 ${className}`}>
    <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" aria-hidden="true" />
  </div>
);

export const isIntegrationFetchFailure = (error) => {
  if (!error) return false;
  const status = error?.response?.status;
  return !status || status === 404 || status >= 500;
};

export const IntegrationFetchError = ({ error, onRetry }) => {
  const status = error?.response?.status;
  const is404 = status === 404;
  const message = is404
    ? integrationT('platformSettings.integrations.common.messages.endpointUnavailable')
    : error?.response?.data?.detail || error?.message || integrationT('platformSettings.integrations.common.messages.saveFailed');

  return (
    <div
      className="max-w-lg mx-auto mt-12 p-6 rounded-xl border border-red-500/30 bg-red-500/10 text-white"
      data-testid="integration-fetch-error"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-red-200">
            {integrationT('platformSettings.integrations.common.status.error')}
          </p>
          <p className="text-sm text-red-300/90 mt-1">{message}</p>
          {onRetry && (
            <Button
              type="button"
              variant="outline"
              className="mt-4 border-red-500/40 text-red-200 hover:bg-red-500/20"
              onClick={onRetry}
            >
              {integrationT('platformSettings.integrations.common.actions.retry')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const IntegrationSetupNotice = ({ providerName }) => (
  <div
    className="mb-6 p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 flex items-start gap-3"
    data-testid="integration-setup-notice"
  >
    <Settings2 className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
    <div>
      <p className="font-medium text-amber-200">
        {integrationT('platformSettings.integrations.common.status.notConfigured')}
        {providerName ? ` — ${providerName}` : ''}
      </p>
      <p className="text-sm text-amber-300/90 mt-1">
        {integrationT('platformSettings.integrations.common.messages.noConfig')}
      </p>
    </div>
  </div>
);
